// =============================================================================
// ingredientWastageSync.js — Meadowhall ingredient-wastage read + write glue.
//
// Sibling of wastageSync.js / selloutSync.js, but with ONE key difference:
// Supabase is the SOURCE OF TRUTH for this feature (there is no legacy Apps
// Script sheet behind it — the old "Fish Tracker" spreadsheet was just the
// template). So unlike the best-effort kitchen-wastage shadow, this SURFACES the
// write result to the caller (ok / error) instead of swallowing it — a failed
// save must be visible to the staffer, never a silent drop (the recurring
// unread-error-body lesson).
//
// Still gated by dualRunEnabled(): on the live Meadowhall host it is ON; on
// staging / a preview build / localhost it returns {skipped:true} and does
// nothing, so those environments behave as read-only mock-ups for visual
// sign-off (force a real end-to-end test with ?dualrun=on).
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, ensurePass } from "./stockSync.js";
import { ensureAnonSession } from "./auth.js";
import { ingredientWastageRowsFrom, mergeForSave, ingredientWastageError } from "./ingredientWastageRows.js";
import { getSite } from "../config/sites.js";

// Full-service sites only (the kitchen is on the premises: Meadowhall, and
// Trafford since the 24 Sep 2026 reopen). Config-driven via sites.js hasKitchen —
// was a hardcoded `siteKey !== "meadowhall"` in three places.
function fullServiceSite(siteKey) {
  const s = getSite(siteKey);
  return !!(s && s.hasKitchen);
}
export { ingredientWastageError }; // re-exported so the tile (which imports this namespace) can validate before save

// Fresh at CALL time (the day-rollover lesson): a kiosk that slept through
// midnight must stamp the new London trading day, not a stale React value.
const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

// Read today's saved figures so the tile prefills before anyone edits — the
// guard against a partial re-save wiping the morning's numbers (the daily row is
// a full-day snapshot, so the tile must show what's already there).
//
// Returns:
//   { ok:true, skipped:true, rows:{} }            — dual-run off (staging/preview)
//   { ok:true, rows: { [ingredient]: {in,used,wasted} } }  — loaded (may be empty)
//   { ok:false, error }                            — load failed; the tile keeps
//                                                    Save DISABLED so it can
//                                                    never overwrite blind.
export async function readIngredientWastage({ siteKey, day = londonToday() } = {}) {
  // Full-service sites only — hard-gate at the data layer too, not just the tile.
  if (!fullServiceSite(siteKey)) return { ok: true, skipped: true, rows: {} };
  if (!dualRunEnabled()) return { ok: true, skipped: true, rows: {} };
  try {
    await ensureAnonSession();
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };
    const { data, error } = await supabase
      .from("ingredient_wastage_daily")
      .select("ingredient,in_g,used_g,wasted_g")
      .eq("site_id", siteId)
      .eq("event_date", day);
    if (error) return { ok: false, error };
    const rows = {};
    (data || []).forEach((r) => {
      rows[r.ingredient] = { in: r.in_g || 0, used: r.used_g || 0, wasted: r.wasted_g || 0 };
    });
    return { ok: true, rows };
  } catch (e) {
    return { ok: false, error: e };
  }
}

// One call per save. `entries` is the tile's per-ingredient figures object
// ({ [ingredient]: {in,used,wasted} }, grams). We shape those into
// ingredient_wastage_daily rows and UPSERT them in one round-trip on the
// (site_id, event_date, ingredient) key — a re-save of the same day updates the
// row rather than duplicating it.
//
// staff_id is NOT sent — the DB stamps it from the pass minted by ensurePass
// (per-action verify, which is what makes the who-saved-it stamp truthful). The
// raw PIN never touches a row.
export async function recordIngredientWastage({ siteKey, staffPin, entries, dirtyFields = null, day = londonToday() }) {
  // Full-service sites only — hard-gate at the data layer too, not just the tile.
  if (!fullServiceSite(siteKey)) return { ok: true, skipped: true };
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  try {
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) return { ok: false, error: "pin" };
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };
    // Re-read + merge so fields this session didn't touch keep their latest
    // saved value (a stale mount snapshot can never wipe another write).
    let toWrite = entries;
    // Ingredients the staffer explicitly touched this session. These are KEPT
    // even if they came out all-zero (an affirmative "checked, nothing wasted"),
    // so a genuine zero-wastage day can be recorded instead of silently dropped.
    let touchedIngs = null;
    if (dirtyFields && dirtyFields.length) {
      const fresh = await readIngredientWastage({ siteKey, day });
      // A failed pre-write read means we can't trust the merge — abort rather than
      // upsert a stale mount snapshot over a figure another device wrote (mirrors
      // recordZeroWastageDay). The staffer sees an error and can retry.
      if (fresh && fresh.ok === false) return { ok: false, error: fresh.error || "load" };
      if (fresh && fresh.ok && fresh.rows) toWrite = mergeForSave(entries, fresh.rows, new Set(dirtyFields));
      touchedIngs = new Set(dirtyFields.map((k) => String(k).split("|")[0]));
    }
    // Final integrity gate: never let a row that breaks In >= Used/Wasted reach
    // the cost report. The tile's own guard only saw `entries`; the merge above
    // can substitute a fresh, lower In for a field this session didn't touch
    // (another tablet corrected In), so the MERGED result must be re-validated.
    for (const name of Object.keys(toWrite)) {
      if (ingredientWastageError(toWrite[name])) return { ok: false, error: "invalid" };
    }
    const rows = ingredientWastageRowsFrom({ siteId, day, entries: toWrite, keepZeroFor: touchedIngs });
    if (!rows.length) return { ok: true, skipped: true };
    const { data, error } = await supabase
      .from("ingredient_wastage_daily")
      .upsert(rows, { onConflict: "site_id,event_date,ingredient" })
      .select();
    if (error) return { ok: false, error };
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

// One-tap "log a zero-waste day". Records an explicit 0/0/0 for the given
// ingredients — the North-Star clean day the floor should be able to affirm in a
// single action, not by tapping every field to zero.
//
// CROSS-DEVICE SAFE by design: it re-reads today's row first and only writes a
// zero for an ingredient that has NOTHING real logged yet. If another save (real
// intake / waste) already landed for an ingredient, that row is LEFT UNTOUCHED —
// a blanket "zero day" must never clobber a weighed figure. If every ingredient
// already has a real log, nothing is written and { allLogged:true } comes back.
// Either way `data` carries the WHOLE day (the zeros just written PLUS the
// already-logged rows left untouched), so the tile reflects the true day in one
// pass — no follow-up reload that could race the status banner.
export async function recordZeroWastageDay({ siteKey, staffPin, ingredients, day = londonToday() }) {
  if (!fullServiceSite(siteKey)) return { ok: true, skipped: true };
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  const names = Array.isArray(ingredients) ? ingredients.filter(Boolean) : [];
  if (!names.length) return { ok: true, skipped: true };
  try {
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) return { ok: false, error: "pin" };
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };
    // Only fill ingredients that have no real figure logged yet — never overwrite.
    const fresh = await readIngredientWastage({ siteKey, day });
    if (fresh && fresh.ok === false) return { ok: false, error: fresh.error || "load" };
    const freshRows = (fresh && fresh.rows) || {};
    const toWrite = {};
    names.forEach((name) => {
      const f = freshRows[name];
      const positive = f && ((f.in || 0) + (f.used || 0) + (f.wasted || 0)) > 0;
      if (!positive) toWrite[name] = { in: 0, used: 0, wasted: 0 };
    });
    // The already-logged rows we deliberately skipped, shaped like DB rows so the
    // tile can show the whole day (skipped positives + the zeros we write below).
    const skipped = names
      .filter((n) => !toWrite[n] && freshRows[n])
      .map((n) => ({ ingredient: n, in_g: freshRows[n].in || 0, used_g: freshRows[n].used || 0, wasted_g: freshRows[n].wasted || 0 }));
    const keep = new Set(Object.keys(toWrite));
    const rows = ingredientWastageRowsFrom({ siteId, day, entries: toWrite, keepZeroFor: keep });
    if (!rows.length) return { ok: true, allLogged: true, data: skipped };
    const { data, error } = await supabase
      .from("ingredient_wastage_daily")
      .upsert(rows, { onConflict: "site_id,event_date,ingredient" })
      .select();
    if (error) return { ok: false, error };
    return { ok: true, data: [...(data || []), ...skipped] };
  } catch (e) {
    return { ok: false, error: e };
  }
}
